import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { ResponseDto, DeleteMembershipTierDto } from "@mimber/schema"
import { membershipTiersKeys } from "../membership-tiers.utils"

type DeleteMembershipTierParams = {
  tierUuid: string
  replacementTierUuid?: string
}

/**
 * Organization-specific hook for deleting a membership tier
 * Can only be used in mimber-organization app
 */
export const useDeleteMembershipTier = (
  options?: UseMutationOptions<
    ResponseDto<void>,
    Error,
    DeleteMembershipTierParams
  >
): UseMutationResult<ResponseDto<void>, Error, DeleteMembershipTierParams> => {
  guardAppUsage("mimber-organization", "useDeleteMembershipTier")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({
      tierUuid,
      replacementTierUuid,
    }: DeleteMembershipTierParams): Promise<ResponseDto<void>> => {
      const body: DeleteMembershipTierDto = {}
      if (replacementTierUuid) {
        body.replacementTierUuid = replacementTierUuid
      }

      const response = await server.delete<ResponseDto<void>>(
        `membership-tiers/${tierUuid}`,
        { data: body }
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate membership tiers queries to refetch the list
      queryClient.invalidateQueries({
        queryKey: membershipTiersKeys.findAll(),
      })
      // Remove the specific tier from cache
      queryClient.removeQueries({
        queryKey: membershipTiersKeys.findOne(variables.tierUuid),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
